import type { ConfigurationSelection } from './types';
import type { CompareKpiSet, ConfigurationSetup } from '@repo/shared-types';

export function buildSelectionLabel(selection: ConfigurationSelection): string {
  return `${selection.configurationName} (v${selection.versionNumber})`;
}

export function getAvailableVersions(
  configurations: ConfigurationSetup[],
  configId: string,
): Array<{ id: string; versionNumber: number }> {
  const config = configurations.find((c) => c.id === configId);
  if (!config?.versions) return [];
  return config.versions
    .map((v) => ({ id: v.id, versionNumber: v.version_number }))
    .sort((a, b) => a.versionNumber - b.versionNumber);
}

let nextGroupId = 0;
export function generateGroupId(): string {
  return `group_${++nextGroupId}`;
}

const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
export function groupLabel(index: number): string {
  if (index < 26) return `Group ${ALPHABET[index]}`;
  return `Group ${index + 1}`;
}

export type AggregatedGroupKpis = CompareKpiSet;

const EMPTY_KPIS: AggregatedGroupKpis = {
  totalCalls: 0,
  bookingsMade: 0,
  bookingsAttended: 0,
  paymentLinksSent: 0,
  paymentLinksPaid: 0,
  financeLinksSent: 0,
};

/**
 * The backend already aggregates each group within its own relative window and
 * returns one KPI set per group id; this just supplies zeros for a group that
 * had no calls (or no selections).
 */
export function groupKpis(kpis: AggregatedGroupKpis | undefined): AggregatedGroupKpis {
  return kpis ?? { ...EMPTY_KPIS };
}

export function getRate(agg: AggregatedGroupKpis, key: string): number | null {
  if (key === 'totalCalls') return null;
  const total = agg.totalCalls;
  if (total === 0) return null;
  return (agg[key as keyof AggregatedGroupKpis] / total) * 100;
}

export function findBestGroupIndex(values: number[], higherIsBetter: boolean): number | null {
  if (values.length === 0) return null;
  const allSame = values.every((v) => v === values[0]);
  if (allSame) return null;

  let bestIdx = 0;
  for (let i = 1; i < values.length; i++) {
    if (higherIsBetter ? values[i] > values[bestIdx] : values[i] < values[bestIdx]) {
      bestIdx = i;
    }
  }
  return bestIdx;
}

export function computeDiffPct(current: number, best: number): number | null {
  if (best === 0) return null;
  return ((current - best) / best) * 100;
}
